import getAvailabilityByRegion from "@utils/getAvailabilityByRegion"
import { getMarketAmount } from "@utils/pricing"
import _ from "lodash-es"
import { useEffect, useMemo, useState } from "react"

type Params = {
  customerCountryCode?: string
  inventoryData: null | Record<string, any>
  location: Location // from props
  mode?: "PDP" | "SOPDP"
  urlKeys?: {
    device?: string
    variant?: string
  }
  variants: Variant[] // raw Contentful variants (already formatted)
}

type Variant = any

export function usePdpVariantState({
  customerCountryCode = "US",
  inventoryData,
  location,
  mode = "PDP",
  urlKeys,
  variants,
}: Params) {
  const VARIANT_KEY = urlKeys?.variant ?? "variant"
  const DEVICE_KEY = urlKeys?.device ?? "device"

  // Filter out variants without images
  const validVariants = useMemo(
    () => _.reject(variants, (v: any) => !v?.images),
    [variants]
  )

  const [validVariantsByStock, setValidVariantsByStock] =
    useState<Variant[]>(validVariants)
  const [activeVariant, setActiveVariant] = useState(0)
  const [searchParamParsed, setSearchParamParsed] = useState(false)
  const [variantSetBySearchParam, setVariantSetBySearchParam] = useState(false)
  const [inventorySorted, setInventorySorted] = useState(false)

  // Parse URL on first mount
  useEffect(() => {
    const search = location?.search || ""
    const params =
      search && typeof URLSearchParams !== "undefined"
        ? new URLSearchParams(search)
        : null

    if (!params) {
      setSearchParamParsed(true)
      return
    }

    const variant = params.get(VARIANT_KEY)?.trim()
    const device = params.get(DEVICE_KEY)?.trim()

    if (variant) {
      // In SOPDP, prefer (sku + deviceName) match; fallback to sku-only
      // In PDP, sku-only
      let i = -1
      if (mode === "SOPDP" && device) {
        i = _.findIndex(
          validVariants,
          (v: any) =>
            v?.sku?.trim() === variant && v?.deviceName?.trim() === device
        )
      }
      if (i === -1) {
        i = _.findIndex(validVariants, (v: any) => v?.sku?.trim() === variant)
      }

      if (i !== -1) {
        setActiveVariant(i)
        setVariantSetBySearchParam(true)
        setSearchParamParsed(true)
        return
      }
    }

    setSearchParamParsed(true)
  }, [])

  // Resort by availability once inventory + country are known
  useEffect(() => {
    if (!inventoryData || !customerCountryCode) return

    const withFlags = validVariants.map((variant: any) => {
      const avail = getAvailabilityByRegion(
        customerCountryCode,
        variant.sku,
        inventoryData,
        {
          mode,
          variantAvailability: variant.availability,
        }
      )
      return { ...variant, __isUnavailable: !avail?.isAvailable }
    })
    const sorted = _.sortBy(withFlags, (v) => v.__isUnavailable)
    setValidVariantsByStock(sorted)

    // If URL selected a specific variant, re-find its index after resort
    if (variantSetBySearchParam) {
      const search = location?.search || ""
      const params =
        search && typeof URLSearchParams !== "undefined"
          ? new URLSearchParams(search)
          : null

      const urlSku = params?.get(VARIANT_KEY)?.trim()
      const urlDevice = params?.get(DEVICE_KEY)?.trim()

      if (urlSku) {
        let i = -1
        if (mode === "SOPDP" && urlDevice) {
          i = _.findIndex(
            sorted,
            (v: any) =>
              v?.sku?.trim() === urlSku && v?.deviceName?.trim() === urlDevice
          )
        }
        if (i === -1) {
          i = _.findIndex(sorted, (v: any) => v?.sku?.trim() === urlSku)
        }
        if (i !== -1 && i !== activeVariant) {
          setActiveVariant(i)
        }
      }
    } else if (!inventorySorted) {
      // First time inventory loads with no URL param: ensure we select index 0
      // This forces a state update even if activeVariant is already 0
      // Sorted array has in-stock variants first, so index 0 is the best default
      setActiveVariant(0)
      setInventorySorted(true)
    } else if (activeVariant !== 0) {
      // Subsequent updates: only change if not already at 0
      setActiveVariant(0)
    }
  }, [inventoryData, customerCountryCode])

  // Keep URL in sync when active variant changes
  useEffect(() => {
    if (!searchParamParsed) return

    // Only sync URL after inventory has loaded and sorted (or if URL param was explicitly set)
    if (!variantSetBySearchParam && !inventorySorted) return

    const v = validVariantsByStock[activeVariant]
    if (typeof window === "undefined" || !v?.sku) return

    if (mode === "SOPDP") {
      const params = new URLSearchParams()
      params.set(VARIANT_KEY, (v.sku || "").trim())
      // If deviceName exists, include it
      if (v?.deviceName) {
        params.set(DEVICE_KEY, (v.deviceName || "").trim())
      }
      const wanted = `?${params.toString()}`
      if (window.location.search !== wanted) {
        window.history?.replaceState({}, "", wanted)
      }
    } else {
      const wanted = `?${VARIANT_KEY}=${encodeURIComponent(v.sku)}`
      if (window.location.search !== wanted) {
        window.history?.replaceState({}, "", wanted)
      }
    }
  }, [
    activeVariant,
    validVariantsByStock,
    searchParamParsed,
    mode,
    variantSetBySearchParam,
    inventorySorted,
  ])

  const currentVariant = validVariantsByStock[activeVariant]
  const fallbackVariant = currentVariant ? undefined : variants?.[0]

  const price = getMarketAmount(currentVariant, customerCountryCode, "price")
  const retailPrice =
    getMarketAmount(currentVariant, customerCountryCode, "compareAtPrice") ||
    undefined

  const validVariantSkus = useMemo(
    () => _.map(validVariantsByStock, "sku"),
    [validVariantsByStock]
  )

  return {
    activeVariant,
    currentVariant,
    fallbackVariant,
    price,
    retailPrice,
    setActiveVariant,
    validVariantsByStock,
    validVariantSkus,
  }
}
